import { useLocation } from "react-router-dom";
import { Link } from "../../app/navigation";
import { useLiveQuery } from "dexie-react-hooks";
import { useService } from "../../app/context";
import { displayTitle } from "../../domain/capture/model";
import { Icon } from "../../ui/Icons";

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
export function Inbox() {
  const { db } = useService();
  const location = useLocation();
  const data = useLiveQuery(
    async () => ({
      captures: await db.captures.orderBy("updatedAt").reverse().toArray(),
      drafts: (await db.drafts.orderBy("updatedAt").reverse().toArray()).filter(
        (d) => !d.deletedAt,
      ),
    }),
    [db],
  );
  if (!data)
    return (
      <main className="page loading" aria-busy="true">
        正在读取本机记录…
      </main>
    );
  const captures = data.captures.filter(
    (c) => !c.deletedAt && !c.archivedAt && c.state === "inbox",
  );
  return (
    <main className="page inbox-page" id="main">
      <div className="eyebrow">你的随手记录</div>
      <div className="page-heading">
        <h1>收件箱</h1>
        <span className="count-label">
          {captures.length ? `${captures.length} 条未整理` : "从一条记录开始"}
        </span>
      </div>
      <p className="page-intro">先存下来，不必马上想好放在哪里。</p>
      <div className="detail-actions">
        <Link to="/home">首页</Link>
        <Link to="/items">全部事项</Link>
        <Link to="/settings/backup">备份与恢复</Link>
        <Link to="/trash">回收站</Link>
      </div>
      {location.state?.saved && (
        <div className="notice success" role="status">
          已保存到收件箱。
        </div>
      )}
      {data.drafts.length > 0 && (
        <section className="draft-section" aria-labelledby="draft-heading">
          <div className="section-heading">
            <h2 id="draft-heading">还没写完</h2>
            <span>{data.drafts.length} 份草稿</span>
          </div>
          <div className="record-list">
            {data.drafts.map((draft) => (
              <Link
                key={draft.id}
                className="record-row draft-row"
                to={`/captures/${draft.captureId}/edit?draft=${draft.id}`}
                state={{ from: "/inbox" }}
              >
                <span className="row-symbol"><Icon name="pen"/></span>
                <div className="record-copy">
                  <strong>
                    {draft.body.trim()
                      ? displayTitle(draft.body)
                      : draft.attachmentIds.length
                        ? "图片草稿"
                        : "空白草稿"}
                  </strong>
                  <p>
                    {draft.state === "conflict"
                      ? "冲突分支 · 双方已保留"
                      : "已保存在本机 · 草稿"}
                    {draft.attachmentIds.length
                      ? ` · ${draft.attachmentIds.length} 张图片`
                      : ""}
                  </p>
                </div>
                <span className="row-chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {!captures.length && !data.drafts.length ? (
        <section className="empty-state">
          <h2>给刚想到的事，留个位置。</h2>
          <p>
            一句话、一个链接，或一张图片。
            <br />
            先放在这里，以后再慢慢整理。
          </p>
          <Link
            to="/capture/new?view=sheet"
            state={{ from: "/inbox" }}
            className="button primary"
          >
            <Icon name="plus" />
            记一下
          </Link>
          <p className="small-note">记录保存在本机，断网也可以继续。</p>
        </section>
      ) : (
        <section aria-labelledby="records-heading">
          <div className="section-heading">
            <h2 id="records-heading">存下的记录</h2>
            <Link
              className="text-link"
              to="/capture/new?view=sheet"
              state={{ from: "/inbox" }}
            >
              ＋ 记一下
            </Link>
          </div>
          {captures.length ? (
            <div className="record-list">
              {captures.map((capture) => (
                <Link
                  className="record-row"
                  key={capture.id}
                  to={`/captures/${capture.id}`}
                  state={{ from: "/inbox" }}
                >
                  <span className="row-symbol">
                    <Icon
                      name={capture.attachmentIds.length ? "image" : "inbox"}
                    />
                  </span>
                  <div className="record-copy">
                    <strong>{displayTitle(capture.body)}</strong>
                    <p>
                      {formatDate(capture.createdAt)}
                      {capture.attachmentIds.length
                        ? ` · ${capture.attachmentIds.length} 张图片`
                        : ""}
                    </p>
                  </div>
                  <span className="row-chevron" aria-hidden="true">
                    ›
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="muted">草稿存下后，会出现在这里。</p>
          )}
        </section>
      )}
      {data.captures.some(c=>!c.deletedAt&&c.state==='organized')&&<section>
        <h2>已整理原记录</h2>
        {data.captures
          .filter((c) => !c.deletedAt && c.state === "organized")
          .map((c) => (
            <Link className="record-row" key={c.id} to={`/captures/${c.id}`}>
              {displayTitle(c.body)}
            </Link>
          ))}
      </section>}

    </main>
  );
}
